// Session state of the offline-copy encryption (OFFLINE_ENCRYPTION) and the
// record sealing used by every Dexie reader/writer. One instance per JS realm:
// the page sets it at boot, the search worker receives mode + keys in `init`.
//
// Plain mode (flag off) passes rows through unchanged, so nothing changes for
// tenants without the flag. Encrypted mode keeps only the fields Dexie indexes
// or compares in clear (ids, partId, hashes, order) and seals the rest of each
// row into `enc`; writing while no key is loaded throws instead of falling
// back to plaintext.
import { importDataKey, opaqueKey, encryptJson, decryptJson } from "./odCrypto.js";

const PLAIN_FIELDS = {
  documents: ["docId", "partId", "contentHash", "metaSig", "familyKey"],
  parts: ["id", "parent_id", "order"],
  documentTypes: ["order"],
  chunks: ["chunkId", "docId"],
  searchIndex: ["name", "builtAt", "formatVersion", "docCount", "chunkCount", "bytes"],
};
const ID_FIELD = { documents: "docId", parts: "id", documentTypes: "order", chunks: "chunkId", searchIndex: "name" };

let encrypted = false;
let keys = null;
const keyListeners = new Set();

export function setEncryptionMode(on) { encrypted = !!on; }
export const isEncrypted = () => encrypted;
export const getKeys = () => keys;
export const hasKeys = () => !!keys;

export async function setRawKey(raw) {
  keys = raw ? await importDataKey(raw) : null;
  keyListeners.forEach((fn) => { try { fn(keys); } catch { /* ignore */ } });
  return keys;
}
export function setKeys(k) {
  keys = k || null;
  keyListeners.forEach((fn) => { try { fn(keys); } catch { /* ignore */ } });
}
export function onKeys(fn) { keyListeners.add(fn); return () => keyListeners.delete(fn); }

function requireKeys() {
  if (!keys) throw new Error("offline copy is locked (no key)");
  return keys;
}

export async function sealRow(table, row) {
  if (!encrypted) return row;
  const k = requireKeys();
  const plain = {};
  const secret = {};
  for (const [f, v] of Object.entries(row)) (PLAIN_FIELDS[table].includes(f) ? plain : secret)[f] = v;
  plain.enc = await encryptJson(k, `${table}|${row[ID_FIELD[table]]}`, secret);
  return plain;
}

export async function openRow(table, row) {
  if (!encrypted || !row || !row.enc) return row;
  const k = requireKeys();
  const { enc, ...plain } = row;
  return { ...plain, ...(await decryptJson(k, `${table}|${row[ID_FIELD[table]]}`, enc)) };
}

export const sealRows = (table, rows) => Promise.all((rows || []).map((r) => sealRow(table, r)));
export const openRows = (table, rows) => Promise.all((rows || []).map((r) => openRow(table, r)));

// Cache key of a document file: the path itself in plain mode, an opaque
// /od/f/<hmac> path in encrypted mode (file names stay out of Cache Storage).
export async function storeKey(logicalKey) {
  if (!encrypted) return logicalKey;
  // Normalised like the URL the service worker sees (spaces, umlauts → %xx).
  const path = new URL(logicalKey, "http://od").pathname;
  return new URL(await opaqueKey(requireKeys(), path, "http://od")).pathname;
}

// Name prefixes: encrypted mode uses its own databases and caches, so the
// switch never mixes plain and sealed rows.
export const dbPrefix = () => (encrypted ? "onlineDokuOfflineE_" : "onlineDokuOffline_");
export const cachePrefix = () => (encrypted ? "odx-" : "od-");
